/**
 * `TrpcBoardDataSource` — the same `BoardDataSource`, over the round-2 A api.
 *
 * The table view never learns which source it is talking to: swapping
 * `createFixtureDataSource()` for `createTrpcDataSource(trpcClient)` is the whole of round 3's
 * wiring. This file is the adapter between our view shapes (§5.1) and the service shapes the
 * api actually serves (`apps/api/src/services/board-read.ts`, `tasks/T-B1.md`).
 *
 * It talks to a narrow **port** (`BoardApi`) rather than to the tRPC proxy directly, for two
 * reasons: the mapping is then unit-testable without a database (`trpc-source.test.ts` drives
 * it with a stub), and the day a procedure is renamed only `createBoardApi` changes.
 *
 * ## What the api already covers
 *
 * | `BoardDataSource`      | procedure |
 * |---|---|
 * | `load`                 | `boards.read` (+ `views.list`, `views.userState`) |
 * | `page`                 | `boards.nextItems` |
 * | `setCellValues`        | `cells.setMany`, one call per item |
 * | `renameItem` …         | `items.rename` / `.create` / `.remove` / `.archive` / `.duplicate` / `.move` |
 * | `setGroupCollapsed`    | `groups.setCollapsed` |
 * | `createColumn` …       | `columns.create` / `.update` / `.setWidth` / `.updateSettings` / `.duplicate` / `.remove` / `.restore` / `.move` |
 * | `saveViewSettings`     | `views.update` |
 * | `setPersonalState`     | `views.setUserState` |
 *
 * `facets` (§5.4) is `boards.facets` as of round 4, which closes open questions 215 and 216:
 * `bucketsOf` moved from the web registry down into `packages/shared`, so the api counts with
 * the **same** type-owned buckets the client filters and groups by, and the quick-filter pane
 * shows real counts on a real board instead of its "no facets" state.
 *
 * The two gaps of `tasks/T-B4.md` are closed as well: `boards.members` gives the avatars real
 * people, and `cells.setMany` returns `previousValue`, which is what the undo toast inverts.
 */
import type { BoardId, ColumnAggregation, ColumnId, GroupId, ItemId, UserId, ViewId } from '@elizmonday/shared';
import {
  defaultAggregationOf,
  findColumnType,
  formatSummaryPair,
  formatSummaryScalar,
} from '@elizmonday/shared';

/** The type's default footer aggregation, `'none'` for an unregistered type (P-027). */
function defaultAggregationOfColumn(type: string): ColumnAggregation {
  const shared = findColumnType(type);
  return shared ? defaultAggregationOf(shared) : 'none';
}
import { normaliseColumnSettings } from '../columns/registry';
import {
  DEFAULT_TABLE_VIEW_SETTINGS,
  overlaySettings,
  sharedSettings,
  type FilterGroup,
  type IgnoredColumnRef,
  type TableViewSettings,
} from '../table/settings';
import { INITIAL_PAGE, MAX_PAGE, PAGE } from './memory';
import type {
  BoardChange,
  BoardColumn,
  GroupPlacement,
  BoardDataSource,
  BoardGroupView,
  BoardViewPayload,
  CellWrite,
  CellWriteResult,
  FacetValue,
  GroupPage,
  ItemRow,
  Person,
  QueryState,
  RemovalResult,
  RestoreInput,
  SummaryValue,
  AccountTag,
} from './types';
import { PostWriteError } from './errors';

/* ── the wire shapes the api serves (apps/api/src/services/board-read.ts) ─── */

export interface ApiColumn {
  id: string;
  type: string;
  title: string;
  description: string | null;
  settingsStr: Record<string, unknown>;
  position: string;
  width: number | null;
  revision: string;
  state: string;
  summary: string;
  editable: boolean;
}

export interface ApiItem {
  id: string;
  groupId: string;
  name: string;
  position: string;
  /** Absent on the mutation payloads that are not board reads (`items.rename`, P-120). */
  columnValues?: Record<string, { value: unknown; text: string | null }>;
  /**
   * **P-056.** The row's active subitems, in their own board order, and its top-level update
   * count — what the grid's expand chevron, its count bubble and the name cell's updates
   * bubble are drawn from. `boards.read` carries both now (round 10c); this client used to
   * hard-code `[]` and `0`, so no real board ever showed any of the three.
   */
  subitemIds?: string[];
  updatesCount?: number;
}

/** `tags.list` — one account tag, ids as strings on the wire like every other id. */
export interface ApiTag {
  id: string;
  name: string;
  color?: string | null;
}

export interface ApiGroup {
  id: string;
  title: string;
  color: string;
  position: string;
  itemCount: number;
  items: ApiItem[];
  cursor: string | null;
  summaries: Record<string, ApiSummary>;
  /** Subitems under this group's items, over the same filtered set as `itemCount` (P-056). */
  subitemCount?: number;
  /** `true` for a `gb:<columnId>:<bucketKey>` bucket produced by group-by (B6 §4.7). */
  virtual?: boolean;
}

export type ApiSummary =
  | { shape: 'scalar'; aggregation: string; value: unknown }
  | { shape: 'pair'; aggregation: string; from: unknown; to: unknown }
  | { shape: 'buckets'; aggregation: string; buckets: Record<string, number> };

export interface ApiBoardRead {
  board: {
    id: string;
    name: string;
    itemTerminology: string | null;
    itemTerminologyPlural: string | null;
  };
  columns: ApiColumn[];
  groups: ApiGroup[];
  /** `Showing <filtered> of <items> items` (B6 §4.2); absent on an older server. */
  totals?: { items: number; filtered: number };
  /**
   * **P-268.** Saved sort / filter / group-by entries the read dropped because the column they
   * name is gone. Absent on an older api, and empty on every board whose saved query is intact.
   */
  ignored?: IgnoredColumnRef[];
  access: {
    canEditContent: boolean;
    canEditStructure: boolean;
    /**
     * `view.update` on a **shared** view, decided by `can()` on the server (P-264). Optional
     * because an older api does not send it; the fallback below is what the client used to
     * infer, and it is wrong for exactly one role — a guest, whom §5.5 denies a shared view.
     */
    canSaveView?: boolean;
    isOwner: boolean;
  };
}

/**
 * What `cells.setMany` answers per column. `previousValue` is the whole reason the undo toast
 * can exist against a real board: only the server knows what was there before, and the
 * service already computes it for the activity row (this closes open question 217).
 */
export interface ApiCellWrite {
  changed: boolean;
  columnId: string;
  value: unknown;
  previousValue: unknown;
  text: string | null;
  previousText: string | null;
}

export interface ApiView {
  id: string;
  name: string;
  type: string;
  isDefault: boolean;
  ownerId: string | null;
  settings?: Record<string, unknown> | null;
}

/** Everything this source needs from the api, and nothing else. */
/** `items.bulkDelete` / `items.bulkArchive` / `items.undoBulk` all answer this shape. */
export interface ApiBulkResult {
  itemIds: string[];
  actionRecordUuid: string;
}

export interface BoardApi {
  readBoard(input: {
    boardId: string;
    viewId?: string;
    pageSize: number;
    filter?: unknown;
    sort?: { columnId: string; direction: 'asc' | 'desc' }[];
    groupBy?: { columnId: string; direction: 'asc' | 'desc'; showEmptyGroups: boolean };
    search?: string;
    withSummaries: boolean;
  }): Promise<ApiBoardRead>;
  nextItems(input: {
    boardId: string;
    groupId: string;
    cursor: string;
    pageSize: number;
    filter?: unknown;
    sort?: { columnId: string; direction: 'asc' | 'desc' }[];
    search?: string;
  }): Promise<{ items: ApiItem[]; cursor: string | null }>;
  listViews(input: { boardId: string }): Promise<ApiView[]>;
  viewUserState(input: { viewId: string }): Promise<Record<string, unknown> | null>;
  setViewUserState(input: { viewId: string; state: Record<string, unknown> }): Promise<unknown>;
  updateView(input: { boardId: string; viewId: string; settings: Record<string, unknown> }): Promise<unknown>;
  boardMembers(input: { boardId: string }): Promise<Person[]>;
  facets(input: {
    boardId: string;
    columnIds?: string[];
    filter?: unknown;
    search?: string;
    hiddenColumnIds: string[];
  }): Promise<Record<string, FacetValue[]>>;
  setCells(input: {
    boardId: string;
    itemId: string;
    columnValues: Record<string, unknown>;
    isUndo?: boolean;
    /** Carried as `x-mutation-id` by the unbatched mutation link (M0-F5 §5.3). */
    mutationId?: string;
  }): Promise<ApiCellWrite[]>;
  /**
   * P-110 — a **range** of cells as one action: one `batch_change_pulses_column_value` row per
   * column instead of one `update_column_value` per cell, and one round trip instead of n.
   */
  setCellsBatch(input: {
    boardId: string;
    writes: { itemId: string; columnId: string; value: unknown }[];
    isUndo?: boolean;
    mutationId?: string;
  }): Promise<{ changed: number; results: (ApiCellWrite & { itemId: string })[] }>;
  createItem(input: {
    boardId: string;
    groupId: string;
    name: string;
    placement?: unknown;
  }): Promise<ApiItem>;
  renameItem(input: { itemId: string; name: string }): Promise<ApiItem>;
  /**
   * **P-122.** The account tag vocabulary. Optional because the procedure that serves it
   * (`tags.list`) is not on every api this client talks to; without it the `tags` picker has
   * no vocabulary, which is the state this card describes.
   */
  accountTags?(input: { boardId: string }): Promise<ApiTag[]>;
  /** `tags.createOrGet` — the picker's `Create "…"` row (P-122). */
  createTag?(input: { boardId: string; name: string }): Promise<ApiTag>;
  /* P-128 — the three affordances that are not cell writes. */
  clickButton(input: { boardId: string; itemId: string; columnId: string }): Promise<unknown>;
  startTimer(input: { boardId: string; itemId: string; columnId: string }): Promise<unknown>;
  stopTimer(input: { boardId: string; itemId: string; columnId: string }): Promise<unknown>;
  addTimerSession(input: {
    boardId: string;
    itemId: string;
    columnId: string;
    startedAt: string;
    endedAt: string;
  }): Promise<unknown>;
  /** The multipart upload route (`add_file_to_column`), not a tRPC procedure. */
  uploadFile(input: { itemId: string; columnId: string; file: File }): Promise<unknown>;
  /** M3.S1: the subitems board's columns plus the subitems of a page of parents, in one call. */
  subitemsOf(input: {
    boardId: string;
    parentItemIds: string[];
  }): Promise<{ boardId: string | null; columns: ApiColumn[]; byParent: Record<string, ApiItem[]> }>;
  createSubitem(input: { parentItemId: string; name: string }): Promise<ApiItem>;
  /** Both answer `{ itemIds, actionRecordUuid }` — the handle the undo toast restores by (D6). */
  removeItems(input: { boardId: string; itemIds: string[] }): Promise<ApiBulkResult>;
  archiveItems(input: { boardId: string; itemIds: string[] }): Promise<ApiBulkResult>;
  /** `items.undoBulk` — one call puts back exactly the set that action removed. */
  undoBulkItems(input: { boardId: string; actionRecordUuid: string }): Promise<unknown>;
  /** The per-item inverses, for a removal this session has no action record for. */
  restoreItem(input: { itemId: string; isUndo?: boolean }): Promise<unknown>;
  unarchiveItem(input: { itemId: string; isUndo?: boolean }): Promise<unknown>;
  duplicateItems(input: { boardId: string; itemIds: string[] }): Promise<unknown>;
  moveItems(input: { boardId: string; itemIds: string[]; groupId: string }): Promise<unknown>;
  /** B10: one call per item — `items.moveToBoard` takes a single `itemId` (monday's shape). */
  moveItemToBoard(input: { itemId: string; boardId: string; groupId: string }): Promise<unknown>;
  convertItemToSubitem(input: { itemId: string; parentItemId: string }): Promise<unknown>;
  /** P-304: the subitem row menu's two own calls (`[uis:05-subitem-row-menu]`). */
  moveSubitemToParent(input: { subitemId: string; parentItemId: string }): Promise<unknown>;
  convertSubitemToItem(input: { subitemId: string }): Promise<unknown>;
  setGroupCollapsed(input: {
    boardId: string;
    /** `'all'` is a value `setCollapsedInput` accepts: it collapses the whole board (P-059). */
    groupId: string;
    collapsed: boolean;
  }): Promise<unknown>;
  /** The groups this user has collapsed on this board, from `group_collapse_state` (P-059). */
  collapsedGroupIds(input: { boardId: string }): Promise<string[]>;
  createGroup(input: { boardId: string; title?: string; placement?: unknown }): Promise<unknown>;
  renameGroup(input: { boardId: string; groupId: string; title: string }): Promise<unknown>;
  setGroupColor(input: { boardId: string; groupId: string; color: string }): Promise<unknown>;
  duplicateGroup(input: {
    boardId: string;
    groupId: string;
    withItems: boolean;
    withUpdates?: boolean;
  }): Promise<unknown>;
  moveGroup(input: { boardId: string; groupId: string; placement: unknown }): Promise<unknown>;
  archiveGroup(input: { boardId: string; groupId: string }): Promise<unknown>;
  removeGroup(input: { boardId: string; groupId: string }): Promise<unknown>;
  reorderItem(input: { itemId: string; placement: unknown }): Promise<unknown>;
  /** `items.move` takes the group **and** the position, so a drag is one call (P-104). */
  moveItemToGroup(input: { itemId: string; groupId: string; placement?: unknown }): Promise<unknown>;
  /** `items.bulkReorder` — the whole selection at one drop point, in order (P-103). */
  reorderItems(input: {
    boardId: string;
    itemIds: string[];
    groupId: string;
    placement: unknown;
  }): Promise<unknown>;
  createColumn(input: {
    boardId: string;
    type: string;
    title: string;
    afterColumnId?: string;
  }): Promise<ApiColumn>;
  updateColumn(input: {
    boardId: string;
    id: string;
    revision: string;
    title?: string;
    description?: string | null;
    width?: number | null;
  }): Promise<ApiColumn>;
  /**
   * **P-129.** `revision` is *required* by `columns.updateSettings` (optimistic concurrency,
   * `[09§A.2]`), and it used to be absent from this signature — so every column settings dialog's
   * *Save* sent an input the router's zod schema rejected and nothing was ever written. The field
   * is part of the contract here so a caller cannot omit it again.
   */
  updateColumnSettings(input: {
    boardId: string;
    id: string;
    settings: unknown;
    revision: string;
  }): Promise<ApiColumn>;
  removeColumn(input: { boardId: string; id: string }): Promise<unknown>;
  /** P-087: the inverse of `removeColumn` — `columns.restore`, cells and all. */
  restoreColumn(input: { boardId: string; id: string }): Promise<unknown>;
  /** P-084: `columns.duplicate`, wired to the column menu at last. */
  duplicateColumn(input: { boardId: string; id: string }): Promise<ApiColumn>;
  moveColumn(input: { boardId: string; id: string; afterColumnId: string | null }): Promise<unknown>;
}

/* ── mapping ──────────────────────────────────────────────────────────────── */

/** `columns.position` is a fractional string on the wire; the grid sorts on a number. */
function positionOf(value: string, fallback: number): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

export function toBoardColumn(column: ApiColumn, index: number): BoardColumn {
  return {
    id: column.id as ColumnId,
    type: column.type,
    title: column.title,
    description: column.description,
    // The decoded `settings_str` **is** the canonical store (M1-B5 §4) — normalised through
    // the type's own schema so a column captured in monday's *other* serialisation (T-5r,
    // T-5t) still reaches the renderer and the editor in one shape.
    settings: normaliseColumnSettings(column.type, column.settingsStr),
    width: column.width,
    position: positionOf(column.position, index),
    revision: column.revision,
    aggregation: (column.summary ??
      defaultAggregationOfColumn(column.type)) as ColumnAggregation,
  };
}

export function toItemRow(item: ApiItem, index: number): ItemRow {
  const values: Record<string, unknown> = {};
  /*
   * **P-120.** `items.rename` answers with the item and **no** `columnValues` map — it is a
   * rename, not a board read — and `Object.entries(undefined)` threw *"Cannot convert undefined
   * or null to object"* out of a mutation that had already succeeded. The mapper tolerates a
   * payload without the map exactly as it already tolerates a missing `updatesCount` /
   * `subitemIds` (P-056): the row keeps whatever the grid already holds for its cells.
   */
  for (const [columnId, cell] of Object.entries(item.columnValues ?? {})) values[columnId] = cell.value;
  return {
    id: item.id as ItemId,
    name: item.name,
    groupId: item.groupId as GroupId,
    position: positionOf(item.position, index),
    // P-056: both come from `boards.read` / `boards.nextItems`; a payload that predates them
    // (an older api, a fixture) still reads as "no subitems, no updates" rather than throwing.
    updatesCount: item.updatesCount ?? 0,
    subitemIds: (item.subitemIds ?? []) as ItemId[],
    values,
  };
}

/**
 * The api's three summary shapes collapse into the one the footer renders.
 *
 * P-027: the text goes through `formatSummaryScalar` / `formatSummaryPair`, never `String()` —
 * the SQL aggregate for a date column is a `timestamptz`, and `String(value)` put
 * `2026-04-15 00:00:00+00` on the first screenful of board `717364248`.
 */
export function toSummary(summary: ApiSummary): SummaryValue {
  if (summary.shape === 'scalar') {
    return {
      aggregation: summary.aggregation as ColumnAggregation,
      text: formatSummaryScalar(summary.aggregation as ColumnAggregation, summary.value),
      detail: { value: summary.value },
    };
  }
  if (summary.shape === 'pair') {
    return {
      aggregation: summary.aggregation as ColumnAggregation,
      text: formatSummaryPair(summary.aggregation as ColumnAggregation, summary.from, summary.to),
      detail: { from: summary.from, to: summary.to },
    };
  }
  const total = Object.values(summary.buckets).reduce((sum, count) => sum + count, 0);
  return {
    aggregation: summary.aggregation as ColumnAggregation,
    text: String(total),
    detail: { buckets: summary.buckets },
  };
}

function toGroup(group: ApiGroup, index: number, collapsed: ReadonlySet<string>): BoardGroupView {
  return {
    id: group.id as GroupId,
    title: group.title,
    color: group.color,
    position: positionOf(group.position, index),
    collapsed: collapsed.has(group.id),
    virtual: group.virtual === true,
    page: {
      items: group.items.map(toItemRow),
      cursor: group.cursor,
      total: group.itemCount,
    },
    summaries: Object.fromEntries(
      Object.entries(group.summaries).map(([columnId, summary]) => [columnId, toSummary(summary)]),
    ),
    subitemCount: group.subitemCount ?? 0,
  };
}

/**
 * The view's `FilterGroup` is a **tree** (`{operator, rules, groups}` with a `compareValue`
 * per rule) and `boards.read` now takes the **same tree** — one recursive shape, one compiler
 * (`apps/api/src/services/filter-compiler.ts`), shared with `/v2 items_page(query_params:)`.
 *
 * Round 3 had to flatten it, because the service only accepted `{operator, rules}`; a nested
 * group whose operator differed from the root's could not be expressed, so `isLossyFilter`
 * detected that case and the client sent **no filter at all** rather than the wrong one. Both
 * functions are gone with the flat shape they existed for: `(A or B) and C` now round-trips.
 *
 * The only translation left is per rule: the pane stores one `compareValue` (a scalar, a list
 * or `null`); the service takes `compareValues: unknown[]`.
 */
export interface ApiFilterRule {
  columnId: string;
  operator: string;
  compareValues: unknown[];
  compareAttribute?: string;
}

export interface ApiFilterGroup {
  operator: 'and' | 'or';
  rules: ApiFilterRule[];
  groups: ApiFilterGroup[];
}

export function toApiFilterGroup(group: FilterGroup | null): ApiFilterGroup | undefined {
  if (!group) return undefined;
  const rules: ApiFilterRule[] = group.rules.map((rule) => ({
    columnId: String(rule.columnId),
    operator: rule.operator,
    compareValues: Array.isArray(rule.compareValue)
      ? [...(rule.compareValue as unknown[])]
      : rule.compareValue === null || rule.compareValue === undefined
        ? []
        : [rule.compareValue],
    ...(rule.compareAttribute === undefined ? {} : { compareAttribute: rule.compareAttribute }),
  }));
  const groups = group.groups
    .map((child) => toApiFilterGroup(child))
    .filter((child): child is ApiFilterGroup => child !== undefined);
  // An empty group filters nothing — the builder shows one before its first rule is filled in.
  if (rules.length === 0 && groups.length === 0) return undefined;
  return { operator: group.operator, rules, groups };
}

function toApiFilter(state: Partial<QueryState>): ApiFilterGroup | undefined {
  return toApiFilterGroup(state.filter ?? null);
}

/** Group by (B6 §4.7) — the service replaces the real groups with the column's buckets. */
function toApiGroupBy(state: Partial<QueryState>) {
  const groupBy = state.groupBy ?? null;
  if (!groupBy) return undefined;
  return {
    columnId: String(groupBy.columnId),
    direction: groupBy.direction,
    showEmptyGroups: groupBy.showEmptyGroups,
  };
}

function toApiSort(state: Partial<QueryState>) {
  return state.sort?.map((rule) => ({ columnId: String(rule.columnId), direction: rule.direction }));
}

function toApiSearch(state: Partial<QueryState>): string | undefined {
  const term = (state.search ?? '').trim();
  return term === '' ? undefined : term;
}

/**
 * The bulk mutations answer `{ itemIds, actionRecordUuid }`. A deployment that has not been
 * restarted yet can answer something older, so the shape is checked rather than trusted: with
 * no action record the undo still works, one `items.restore` per id.
 */
function toRemovalResult(result: unknown, requested: readonly ItemId[]): RemovalResult {
  const record = (result ?? {}) as Partial<ApiBulkResult>;
  const itemIds = Array.isArray(record.itemIds)
    ? (record.itemIds.map(String) as ItemId[])
    : [...requested];
  const uuid = typeof record.actionRecordUuid === 'string' ? record.actionRecordUuid : null;
  return { itemIds, actionRecordUuid: uuid };
}

/**
 * The ids `groups.setCollapsed` reports as collapsed **after** the write (P-059). The router
 * hands back the service's `MutationResult`, so the ids are one level in; a source that
 * returns nothing useful yields `null` and the caller falls back to what it asked for.
 */
function collapsedIdsOf(result: unknown): string[] | null {
  const data = (result as { data?: unknown } | null)?.data ?? result;
  const ids = (data as { collapsed?: unknown } | null)?.collapsed;
  if (!Array.isArray(ids)) return null;
  return ids.filter((id): id is string => typeof id === 'string');
}

/** L10: nothing may precede `name`, so the api reads it and `null` as the same "leftmost". */
const NAME_COLUMN_ID = 'name';

/**
 * The service's `Placement` for "directly after this row", or `'top'` when there is no row to
 * be after — `after_at` with no anchor is the first position (`[B3§5]`).
 */
function placementAfter(afterItemId: ItemId | null): unknown {
  return afterItemId ? { method: 'after_at', relativeTo: String(afterItemId) } : 'top';
}

/* ── the source ───────────────────────────────────────────────────────────── */

export class TrpcBoardDataSource implements BoardDataSource {
  private readonly api: BoardApi;
  private readonly me: UserId;
  private readonly listeners = new Set<(change: BoardChange) => void>();
  private readonly collapsed = new Map<string, Set<string>>();
  /** `views.update` needs the board; the interface's `saveViewSettings` only names the view. */
  private boardId: string | null = null;
  /**
   * **P-101.** The board's column ids in position order, refreshed by every `load()`.
   *
   * The grid speaks "before" — it paints a 2 px line at the left edge of the column the row
   * will precede — and the api speaks "after" (`afterColumnId`, with `null` meaning *directly
   * after `name`*, since nothing may precede it, `[09§A.4]`, L10). The two `null`s therefore
   * mean **opposite ends of the board**, which is why the untranslated value sent a column to
   * the far left. The translation needs the current order, and this is the only thing in the
   * source that needs to remember anything about it: the store re-reads the board after every
   * structural mutation, and every read goes through `load()`.
   */
  private columnOrder: string[] = [];

  constructor(api: BoardApi, me: UserId) {
    this.api = api;
    this.me = me;
  }

  async load(input: {
    boardId: BoardId;
    viewId: ViewId;
    state?: Partial<QueryState>;
  }): Promise<BoardViewPayload> {
    const state = input.state ?? {};
    this.boardId = input.boardId;
    /*
     * **P-059.** The collapsed set used to live in a per-instance `Map` and nowhere else, so
     * collapsing a group was forgotten by a reload and invisible to a second tab — while
     * `groups.setCollapsed` had been writing `group_collapse_state` per user and group all
     * along and `groups.collapsed` had been reading it back with nobody asking. The stored
     * set is the truth on load; the local one is only what an in-flight toggle has painted,
     * and it wins while the read is still catching up with it.
     */
    const [read, views, personal, people, storedCollapsed, tags] = await Promise.all([
      this.api.readBoard({
        boardId: input.boardId,
        viewId: input.viewId,
        pageSize: INITIAL_PAGE,
        filter: toApiFilter(state),
        sort: toApiSort(state),
        groupBy: toApiGroupBy(state),
        ...(toApiSearch(state) === undefined ? {} : { search: toApiSearch(state)! }),
        withSummaries: true,
      }),
      this.api.listViews({ boardId: input.boardId }),
      this.api.viewUserState({ viewId: input.viewId }).catch(() => null),
      this.api.boardMembers({ boardId: input.boardId }).catch(() => [] as Person[]),
      this.api.collapsedGroupIds({ boardId: input.boardId }).catch(() => null),
      // P-122: the sixth parallel read. An api without `tags.list` leaves the vocabulary empty
      // rather than failing the board load.
      this.api.accountTags?.({ boardId: input.boardId }).catch(() => [] as ApiTag[]) ?? [],
    ]);

    const view = views.find((entry) => entry.id === input.viewId);
    const saved: TableViewSettings = {
      ...DEFAULT_TABLE_VIEW_SETTINGS,
      ...((view?.settings ?? {}) as Partial<TableViewSettings>),
    };
    const collapsedSet = this.collapsedFor(input.viewId, storedCollapsed);
    const effective: TableViewSettings = {
      ...overlaySettings(saved, (personal ?? {}) as Partial<TableViewSettings>),
      collapsedGroupIds: [...collapsedSet] as GroupId[],
    };

    const columns = read.columns
      .filter((column) => column.state !== 'archived' && column.state !== 'deleted')
      .map(toBoardColumn);
    this.columnOrder = columns.map((column) => String(column.id));

    return {
      board: {
        id: read.board.id as BoardId,
        name: read.board.name,
        itemTerminology: read.board.itemTerminology ?? 'item',
        itemTerminologyPlural: read.board.itemTerminologyPlural ?? 'items',
      },
      columns,
      views: views.map((entry) => ({
        id: entry.id as ViewId,
        name: entry.name,
        type: entry.type,
        isDefault: entry.isDefault,
        ownerId: entry.ownerId as UserId | null,
      })),
      view: { id: input.viewId, settings: saved, effectiveSettings: effective },
      /* P-268: what the server refused to apply, so the store can forget it (§5.1). */
      ignored: read.ignored ?? [],
      groups: read.groups.map((group, index) => toGroup(group, index, collapsedSet)),
      /*
       * Both numbers come from the service now (B6 §5): `itemCount` per group is the
       * **filtered** count, so summing it could only ever produce `filtered`, and the pane's
       * `Showing 6 of 38 items` therefore always read `Showing 38 of 38`.
       */
      totals: read.totals ?? {
        items: read.groups.reduce((sum, group) => sum + group.itemCount, 0),
        filtered: read.groups.reduce((sum, group) => sum + group.itemCount, 0),
      },
      people,
      tags: tags.map((tag) => ({ id: Number(tag.id), name: tag.name, color: tag.color ?? null })),
      permissions: {
        /*
         * P-264: the server answers this from the permission matrix. The client used to infer
         * it as `isOwner || canEditStructure`, which is the same answer for every role except
         * the one §5.5 marks ✖ for a **shared** view — a guest, who was offered "Save to this
         * view" on an `everyone` board and got a 403 for clicking it.
         */
        canSaveView: read.access.canSaveView ?? (read.access.isOwner || read.access.canEditStructure),
        canEditStructure: read.access.canEditStructure,
        /** P-266: the row's `+` is painted only for an actor the matrix lets write items. */
        canEditContent: read.access.canEditContent,
        readOnlyColumnIds: read.columns
          .filter((column) => !column.editable)
          .map((column) => column.id as ColumnId),
      },
      me: this.me,
    };
  }

  async page(input: {
    boardId: BoardId;
    viewId: ViewId;
    groupId: GroupId;
    cursor: string | null;
    limit?: number;
    state?: Partial<QueryState>;
  }): Promise<GroupPage> {
    if (!input.cursor) return { items: [], cursor: null, total: 0 };
    const state = input.state ?? {};
    const page = await this.api.nextItems({
      boardId: input.boardId,
      groupId: input.groupId,
      cursor: input.cursor,
      pageSize: Math.min(input.limit ?? PAGE, MAX_PAGE),
      filter: toApiFilter(state),
      sort: toApiSort(state),
      ...(toApiSearch(state) === undefined ? {} : { search: toApiSearch(state)! }),
    });
    return { items: page.items.map(toItemRow), cursor: page.cursor, total: page.items.length };
  }

  /**
   * §5.4. The counts come from `boards.facets`, which strips each column's own rules from the
   * filter before counting it — so picking a second value in one column widens the result.
   * Hidden columns are passed through because they leave the pane with the grid (§F7).
   */
  async facets(input: {
    boardId: BoardId;
    viewId: ViewId;
    columnIds?: readonly ColumnId[];
    state?: Partial<QueryState>;
  }): Promise<Record<string, FacetValue[]>> {
    const state = input.state ?? {};
    return this.api.facets({
      boardId: String(input.boardId),
      ...(input.columnIds ? { columnIds: input.columnIds.map(String) } : {}),
      filter: toApiFilter(state),
      ...(toApiSearch(state) === undefined ? {} : { search: toApiSearch(state)! }),
      hiddenColumnIds: (state.hiddenColumnIds ?? []).map(String),
    });
  }

  async setCellValues(input: {
    boardId: BoardId;
    writes: readonly CellWrite[];
    isUndo?: boolean;
    mutationId?: string;
  }): Promise<CellWriteResult> {
    // `cells.setMany` takes one item at a time, so a rectangle becomes one call per row.
    const byItem = new Map<string, Record<string, unknown>>();
    for (const write of input.writes) {
      const existing = byItem.get(write.itemId) ?? {};
      existing[write.columnId] = write.value;
      byItem.set(write.itemId, existing);
    }
    const written: CellWrite[] = [];
    const skipped: { itemId: ItemId; columnId: ColumnId; reason: string }[] = [];
    const undo: CellWrite[] = [];

    /*
     * **P-110.** A write that spans more than one item is a **range** — the grid's `Delete`
     * over a selection, or a paste of a TSV block — and the user performed one action, so it
     * goes as one: `cells.setBatch` writes them under one `action_record_uuid` and logs
     * monday's `batch_change_pulses_column_value` per column `[ba§2]` instead of one
     * `update_column_value` per cell. One item (an editor commit, the item card's save) still
     * takes `cells.setMany`, which is the call monday's own `change_multiple_column_values`
     * maps to.
     */
    if (byItem.size > 1) {
      try {
        const batch = await this.api.setCellsBatch({
          boardId: input.boardId,
          writes: input.writes.map((write) => ({
            itemId: String(write.itemId),
            columnId: String(write.columnId),
            value: write.value,
          })),
          ...(input.mutationId ? { mutationId: input.mutationId } : {}),
          isUndo: input.isUndo,
        });
        for (const result of batch.results ?? []) {
          if (!result.changed) continue;
          written.push({
            itemId: result.itemId as ItemId,
            columnId: result.columnId as ColumnId,
            value: result.value,
          });
          undo.push({
            itemId: result.itemId as ItemId,
            columnId: result.columnId as ColumnId,
            value: result.previousValue ?? null,
          });
        }
      } catch (error) {
        for (const write of input.writes) {
          skipped.push({
            itemId: write.itemId,
            columnId: write.columnId,
            reason: error instanceof Error ? error.message : 'write refused',
          });
        }
      }
      this.emit({ kind: 'cells', writes: written });
      return { written, skipped, undo };
    }

    for (const [itemId, columnValues] of byItem) {
      try {
        const results = await this.api.setCells({
          boardId: input.boardId,
          itemId,
          columnValues,
          ...(input.mutationId ? { mutationId: input.mutationId } : {}),
          isUndo: input.isUndo,
        });
        // The service applies the no-op gate (B5 §5.1): an unchanged column returns
        // `changed: false` and contributes neither a write nor an undo step.
        for (const result of results ?? []) {
          if (!result.changed) continue;
          written.push({
            itemId: itemId as ItemId,
            columnId: result.columnId as ColumnId,
            value: result.value,
          });
          undo.push({
            itemId: itemId as ItemId,
            columnId: result.columnId as ColumnId,
            value: result.previousValue ?? null,
          });
        }
      } catch (error) {
        for (const columnId of Object.keys(columnValues)) {
          skipped.push({
            itemId: itemId as ItemId,
            columnId: columnId as ColumnId,
            reason: error instanceof Error ? error.message : 'write refused',
          });
        }
      }
    }
    this.emit({ kind: 'cells', writes: written });
    return { written, skipped, undo };
  }

  async renameItem(input: { boardId: BoardId; itemId: ItemId; name: string }): Promise<ItemRow> {
    const item = await this.api.renameItem({ itemId: input.itemId, name: input.name });
    this.emit({ kind: 'items' });
    // P-120: past this line the rename has happened. Anything that fails here is a bug in this
    // mapper, and the caller must not mistake it for a name the server refused.
    try {
      return toItemRow(item, 0);
    } catch (error) {
      throw new PostWriteError('items.rename', error);
    }
  }

  /* ── the three affordances that are not value writes (P-128) ───────────── */

  async clickButton(input: { boardId: BoardId; itemId: ItemId; columnId: ColumnId }): Promise<void> {
    await this.api.clickButton({
      boardId: String(input.boardId),
      itemId: String(input.itemId),
      columnId: String(input.columnId),
    });
    // The value is the server's to compute (a click count, a duration, a file list), so the
    // subscribers re-read rather than being handed one.
    this.emit({ kind: 'items' });
  }

  async setTimerRunning(input: {
    boardId: BoardId;
    itemId: ItemId;
    columnId: ColumnId;
    running: boolean;
  }): Promise<void> {
    const ref = {
      boardId: String(input.boardId),
      itemId: String(input.itemId),
      columnId: String(input.columnId),
    };
    await (input.running ? this.api.startTimer(ref) : this.api.stopTimer(ref));
    // The value is the server's to compute (a click count, a duration, a file list), so the
    // subscribers re-read rather than being handed one.
    this.emit({ kind: 'items' });
  }

  async addTimerSession(input: {
    boardId: BoardId;
    itemId: ItemId;
    columnId: ColumnId;
    startedAt: string;
    endedAt: string;
  }): Promise<void> {
    await this.api.addTimerSession({
      boardId: String(input.boardId),
      itemId: String(input.itemId),
      columnId: String(input.columnId),
      startedAt: input.startedAt,
      endedAt: input.endedAt,
    });
    // The value is the server's to compute (a click count, a duration, a file list), so the
    // subscribers re-read rather than being handed one.
    this.emit({ kind: 'items' });
  }

  async createTag(input: { boardId: BoardId; name: string }): Promise<AccountTag> {
    if (!this.api.createTag) throw new Error('This account cannot create tags');
    const tag = await this.api.createTag({ boardId: String(input.boardId), name: input.name });
    // The vocabulary changed, not a cell: the board re-reads it on the next load.
    this.emit({ kind: 'items' });
    return { id: Number(tag.id), name: tag.name, color: tag.color ?? null };
  }

  async uploadCellFile(input: {
    boardId: BoardId;
    itemId: ItemId;
    columnId: ColumnId;
    file: File;
  }): Promise<void> {
    await this.api.uploadFile({
      itemId: String(input.itemId),
      columnId: String(input.columnId),
      file: input.file,
    });
    // The value is the server's to compute (a click count, a duration, a file list), so the
    // subscribers re-read rather than being handed one.
    this.emit({ kind: 'items' });
  }

  async createItem(input: {
    boardId: BoardId;
    groupId: GroupId;
    name: string;
    placement?: 'top' | 'bottom' | { afterItemId: ItemId };
  }): Promise<ItemRow> {
    const item = await this.api.createItem({
      boardId: input.boardId,
      groupId: input.groupId,
      name: input.name,
      placement:
        typeof input.placement === 'object'
          ? { relativeTo: input.placement.afterItemId, method: 'after_at' }
          : (input.placement ?? 'bottom'),
    });
    this.emit({ kind: 'items' });
    return toItemRow(item, 0);
  }

  /* ── subitems (M3.S1 §5) ───────────────────────────────────────────────── */

  async subitemsOf(input: {
    boardId: BoardId;
    parentItemIds: readonly ItemId[];
  }): Promise<{
    boardId: BoardId | null;
    columns: readonly BoardColumn[];
    byParent: Record<string, readonly ItemRow[]>;
  }> {
    if (input.parentItemIds.length === 0) return { boardId: null, columns: [], byParent: {} };
    const read = await this.api.subitemsOf({
      boardId: input.boardId,
      parentItemIds: [...input.parentItemIds],
    });
    return {
      // P-111: the nested `+` adds a column to the **subitems** board, so its id travels too.
      boardId: (read.boardId ?? null) as BoardId | null,
      columns: read.columns.map(toBoardColumn),
      byParent: Object.fromEntries(
        Object.entries(read.byParent).map(([parentId, rows], parentIndex) => [
          parentId,
          rows.map((row, index) => toItemRow(row, parentIndex * 1000 + index)),
        ]),
      ),
    };
  }

  async createSubitem(input: {
    boardId: BoardId;
    parentItemId: ItemId;
    name: string;
  }): Promise<ItemRow> {
    const created = await this.api.createSubitem({
      parentItemId: input.parentItemId,
      name: input.name,
    });
    this.emit({ kind: 'items' });
    return toItemRow(created, 0);
  }

  async deleteItems(input: {
    boardId: BoardId;
    itemIds: readonly ItemId[];
  }): Promise<RemovalResult> {
    const result = await this.api.removeItems({
      boardId: input.boardId,
      itemIds: [...input.itemIds],
    });
    this.emit({ kind: 'items' });
    return toRemovalResult(result, input.itemIds);
  }

  async archiveItems(input: {
    boardId: BoardId;
    itemIds: readonly ItemId[];
  }): Promise<RemovalResult> {
    const result = await this.api.archiveItems({
      boardId: input.boardId,
      itemIds: [...input.itemIds],
    });
    this.emit({ kind: 'items' });
    return toRemovalResult(result, input.itemIds);
  }

  /**
   * The undo toast (P-050). With the action record it is **one** call — `items.undoBulk`
   * restores exactly that action's set, marked `is_undo_action` (`[B3 D6]`), which matters:
   * the selection can hold 500 rows. Without one it falls back to `items.restore` per id.
   */
  async restoreItems(input: RestoreInput): Promise<void> {
    await this.putBack(input, 'restore');
  }

  async unarchiveItems(input: RestoreInput): Promise<void> {
    await this.putBack(input, 'unarchive');
  }

  private async putBack(input: RestoreInput, kind: 'restore' | 'unarchive'): Promise<void> {
    if (input.actionRecordUuid) {
      await this.api.undoBulkItems({
        boardId: input.boardId,
        actionRecordUuid: input.actionRecordUuid,
      });
    } else {
      for (const itemId of input.itemIds) {
        if (kind === 'restore') await this.api.restoreItem({ itemId, isUndo: true });
        else await this.api.unarchiveItem({ itemId, isUndo: true });
      }
    }
    this.emit({ kind: 'items' });
  }

  async duplicateItems(input: { boardId: BoardId; itemIds: readonly ItemId[] }): Promise<void> {
    await this.api.duplicateItems({ boardId: input.boardId, itemIds: [...input.itemIds] });
    this.emit({ kind: 'items' });
  }

  async moveItems(input: {
    boardId: BoardId;
    itemIds: readonly ItemId[];
    groupId: GroupId;
  }): Promise<void> {
    await this.api.moveItems({
      boardId: input.boardId,
      itemIds: [...input.itemIds],
      groupId: input.groupId,
    });
    this.emit({ kind: 'items' });
  }

  async moveItemsToBoard(input: {
    boardId: BoardId;
    itemIds: readonly ItemId[];
    targetBoardId: BoardId;
    targetGroupId: GroupId;
  }): Promise<void> {
    // Sequential on purpose: each move re-maps columns server-side and the second call must
    // see the first one's rows, which a parallel `Promise.all` does not guarantee.
    for (const itemId of input.itemIds) {
      await this.api.moveItemToBoard({
        itemId: String(itemId),
        boardId: String(input.targetBoardId),
        groupId: String(input.targetGroupId),
      });
    }
    this.emit({ kind: 'items' });
  }

  async convertItemsToSubitems(input: {
    boardId: BoardId;
    itemIds: readonly ItemId[];
    parentItemId: ItemId;
  }): Promise<void> {
    for (const itemId of input.itemIds) {
      await this.api.convertItemToSubitem({
        itemId: String(itemId),
        parentItemId: String(input.parentItemId),
      });
    }
    this.emit({ kind: 'items' });
  }

  /** P-304 — `Move to Item ▸`: one subitem, one new parent on the same board. */
  async moveSubitemToParent(input: {
    boardId: BoardId;
    subitemId: ItemId;
    parentItemId: ItemId;
  }): Promise<void> {
    await this.api.moveSubitemToParent({
      subitemId: String(input.subitemId),
      parentItemId: String(input.parentItemId),
    });
    this.emit({ kind: 'items' });
  }

  /** P-304 — `Convert to item`: the subitem becomes a top-level item on the parent's board. */
  async convertSubitemToItem(input: { boardId: BoardId; subitemId: ItemId }): Promise<void> {
    await this.api.convertSubitemToItem({ subitemId: String(input.subitemId) });
    this.emit({ kind: 'items' });
  }

  async setGroupCollapsed(input: {
    boardId: BoardId;
    viewId: ViewId;
    groupId: GroupId | 'all';
    collapsed: boolean;
  }): Promise<void> {
    /*
     * P-059: `'all'` used to **clear** the local set and skip the api entirely, so monday's
     * `Collapse all groups` expanded the board instead of collapsing it. It is one call like
     * any other, and the ids the mutation returns are what the local set becomes — the api
     * knows which groups the board actually has, and this client may not.
     */
    const result = await this.api.setGroupCollapsed({
      boardId: input.boardId,
      groupId: input.groupId,
      collapsed: input.collapsed,
    });
    const written = collapsedIdsOf(result);
    if (written !== null) {
      this.collapsed.set(input.viewId, new Set(written));
    } else {
      // A source that reports nothing back: paint what was asked for and let the reload that
      // follows re-seed the set from `groups.collapsed`, which is the record either way.
      const set = this.collapsed.get(input.viewId) ?? new Set<string>();
      if (input.groupId === 'all') set.clear();
      else if (input.collapsed) set.add(input.groupId);
      else set.delete(input.groupId);
      this.collapsed.set(input.viewId, set);
    }
    this.emit({ kind: 'view' });
  }

  /**
   * The collapsed set for a view (P-059).
   *
   * `group_collapse_state` is the record, so a freshly mounted source — a reload, a second tab
   * — starts from it and a collapsed group stays collapsed. Once *this* source has toggled
   * something the local set is at least as fresh as any read (`setGroupCollapsed` sets it from
   * the ids the mutation reports), so it wins: a read that raced the mutation must not
   * un-paint it.
   */
  private collapsedFor(viewId: ViewId, stored: readonly string[] | null): Set<string> {
    const local = this.collapsed.get(viewId);
    if (local !== undefined) return local;
    const set = new Set(stored ?? []);
    this.collapsed.set(viewId, set);
    return set;
  }

  /* ── group lifecycle (`[B2§5]`) ─────────────────────────────────────────── */

  async createGroup(input: {
    boardId: BoardId;
    title?: string;
    placement?: GroupPlacement;
  }): Promise<void> {
    await this.api.createGroup({
      boardId: input.boardId,
      ...(input.title === undefined ? {} : { title: input.title }),
      placement: input.placement ?? 'bottom',
    });
    this.emit({ kind: 'items' });
  }

  async renameGroup(input: { boardId: BoardId; groupId: GroupId; title: string }): Promise<void> {
    await this.api.renameGroup({ boardId: input.boardId, groupId: input.groupId, title: input.title });
    this.emit({ kind: 'items' });
  }

  async setGroupColor(input: { boardId: BoardId; groupId: GroupId; color: string }): Promise<void> {
    await this.api.setGroupColor({ boardId: input.boardId, groupId: input.groupId, color: input.color });
    this.emit({ kind: 'items' });
  }

  async duplicateGroup(input: {
    boardId: BoardId;
    groupId: GroupId;
    withItems: boolean;
    withUpdates?: boolean;
  }): Promise<void> {
    await this.api.duplicateGroup({
      boardId: input.boardId,
      groupId: input.groupId,
      withItems: input.withItems,
      /** P-058: monday's second row copies the items' updates with them. */
      withUpdates: input.withUpdates === true,
    });
    this.emit({ kind: 'items' });
  }

  async moveGroup(input: {
    boardId: BoardId;
    groupId: GroupId;
    placement: GroupPlacement;
  }): Promise<void> {
    await this.api.moveGroup({
      boardId: input.boardId,
      groupId: input.groupId,
      placement: input.placement,
    });
    this.emit({ kind: 'items' });
  }

  async archiveGroup(input: { boardId: BoardId; groupId: GroupId }): Promise<void> {
    await this.api.archiveGroup({ boardId: input.boardId, groupId: input.groupId });
    this.emit({ kind: 'items' });
  }

  async deleteGroup(input: { boardId: BoardId; groupId: GroupId }): Promise<void> {
    await this.api.removeGroup({ boardId: input.boardId, groupId: input.groupId });
    this.emit({ kind: 'items' });
  }

  /**
   * **P-104.** A row drag is **one** service call, as monday's `change_item_position` is:
   * `items.move` carries the group *and* the placement when the group changed, and
   * `items.reorder` carries the placement alone when it did not. It used to send both,
   * unconditionally — so every watcher painted the group's default slot first and the dropped
   * position second, and a reorder inside one group cost two round trips and two
   * `item.moved` events for a gesture monday expresses as one.
   *
   * `after_at` with no anchor means "first", which is `'top'` in `Placement`.
   */
  async reorderItem(input: {
    boardId: BoardId;
    itemId: ItemId;
    groupId: GroupId;
    afterItemId: ItemId | null;
    fromGroupId?: GroupId | null;
  }): Promise<void> {
    const placement = placementAfter(input.afterItemId);
    const sameGroup =
      input.fromGroupId !== undefined &&
      input.fromGroupId !== null &&
      String(input.fromGroupId) === String(input.groupId);
    if (sameGroup) await this.api.reorderItem({ itemId: input.itemId, placement });
    else
      await this.api.moveItemToGroup({
        itemId: input.itemId,
        groupId: input.groupId,
        placement,
      });
    this.emit({ kind: 'items' });
  }

  /**
   * **P-103.** The whole selection, one drop point, one mutation — `items.bulkReorder` places
   * the ids in the order given, so the rows keep the relative order they had on the board.
   */
  async reorderItems(input: {
    boardId: BoardId;
    itemIds: readonly ItemId[];
    groupId: GroupId;
    afterItemId: ItemId | null;
  }): Promise<void> {
    if (input.itemIds.length === 0) return;
    await this.api.reorderItems({
      boardId: input.boardId,
      itemIds: input.itemIds.map(String),
      groupId: input.groupId,
      placement: placementAfter(input.afterItemId),
    });
    this.emit({ kind: 'items' });
  }

  async createColumn(input: {
    boardId: BoardId;
    type: string;
    title: string;
    afterColumnId: ColumnId | null;
  }): Promise<BoardColumn> {
    const column = await this.api.createColumn({
      boardId: input.boardId,
      type: input.type,
      title: input.title,
      ...(input.afterColumnId ? { afterColumnId: input.afterColumnId } : {}),
    });
    this.emit({ kind: 'columns' });
    return toBoardColumn(column, 0);
  }

  async updateColumn(input: {
    boardId: BoardId;
    columnId: ColumnId;
    revision: string;
    title?: string;
    description?: string | null;
    width?: number;
    settings?: unknown;
    aggregation?: ColumnAggregation;
  }): Promise<BoardColumn> {
    let column: ApiColumn;
    if (input.settings !== undefined) {
      column = await this.api.updateColumnSettings({
        boardId: input.boardId,
        id: input.columnId,
        settings: input.settings,
        // P-129: `columns.updateSettings` requires it, exactly like `columns.update`.
        revision: input.revision,
      });
    } else {
      column = await this.api.updateColumn({
        boardId: input.boardId,
        id: input.columnId,
        revision: input.revision,
        ...(input.title === undefined ? {} : { title: input.title }),
        ...(input.description === undefined ? {} : { description: input.description }),
        ...(input.width === undefined ? {} : { width: input.width }),
      });
    }
    this.emit({ kind: 'columns' });
    return toBoardColumn(column, 0);
  }

  async deleteColumn(input: { boardId: BoardId; columnId: ColumnId }): Promise<void> {
    await this.api.removeColumn({ boardId: input.boardId, id: input.columnId });
    this.emit({ kind: 'columns' });
  }

  /** P-087: `columns.remove` is a soft delete, so this brings the cells back with the column. */
  async restoreColumn(input: { boardId: BoardId; columnId: ColumnId }): Promise<void> {
    await this.api.restoreColumn({ boardId: input.boardId, id: input.columnId });
    this.emit({ kind: 'columns' });
  }

  /** P-084: `columns.duplicate`, which had no caller in the product until round 10e. */
  async duplicateColumn(input: { boardId: BoardId; columnId: ColumnId }): Promise<BoardColumn> {
    const column = await this.api.duplicateColumn({ boardId: input.boardId, id: input.columnId });
    this.emit({ kind: 'columns' });
    return toBoardColumn(column, 0);
  }

  /**
   * **P-101.** The api positions **after** a column; the grid drops **before** one. This is
   * the one place that translates, and it is where the comment always said it belonged.
   *
   *   `beforeColumnId = X`    → the id that precedes X once the dragged column is taken out
   *   `beforeColumnId = null` → "no column follows it", i.e. **last**: the final column's id
   *   nothing precedes it     → `null`, which the api reads as *directly after `name`*
   *
   * A drop that resolves to the column's own current predecessor changes nothing, and monday
   * writes no activity row for a move that does not move: it is dropped here rather than sent
   * (the api would answer `404` for the self-relative case, which is what P-100's left-half
   * drop produced).
   */
  async moveColumn(input: {
    boardId: BoardId;
    columnId: ColumnId;
    beforeColumnId: ColumnId | null;
  }): Promise<void> {
    const moved = String(input.columnId);
    const order = this.columnOrder.length > 0 ? this.columnOrder : [];
    const rest = order.filter((id) => id !== moved);
    const before = input.beforeColumnId === null ? null : String(input.beforeColumnId);
    let afterColumnId: string | null;
    if (before === null) {
      afterColumnId = rest[rest.length - 1] ?? null;
    } else {
      const index = rest.indexOf(before);
      // An order we do not hold (no `load()` yet) falls back to the value as it was given.
      afterColumnId = index < 0 ? before : (rest[index - 1] ?? null);
    }
    if (afterColumnId === NAME_COLUMN_ID) afterColumnId = null;
    const current = order.indexOf(moved);
    const previous = current > 0 ? (order[current - 1] ?? null) : null;
    const settled = previous === NAME_COLUMN_ID ? null : previous;
    if (current >= 0 && afterColumnId === settled) return;

    await this.api.moveColumn({
      boardId: input.boardId,
      id: input.columnId,
      afterColumnId,
    });
    this.emit({ kind: 'columns' });
  }

  async saveViewSettings(input: { viewId: ViewId; settings: TableViewSettings }): Promise<void> {
    if (this.boardId === null) throw new Error('load the board before saving its view');
    await this.api.updateView({
      boardId: this.boardId,
      viewId: input.viewId,
      settings: sharedSettings(input.settings) as unknown as Record<string, unknown>,
    });
    this.emit({ kind: 'view' });
  }

  async setPersonalState(input: {
    viewId: ViewId;
    state: Partial<TableViewSettings>;
  }): Promise<void> {
    await this.api.setViewUserState({
      viewId: input.viewId,
      state: input.state as unknown as Record<string, unknown>,
    });
  }

  subscribe(listener: (change: BoardChange) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /**
   * **P-140.** The board room's own change stream, pushed in by `useBoardViewRealtime`. It is
   * the same fan-out as a mutation's `emit`, because a subscriber cannot tell — and must not
   * care — whether the row it has to re-read was changed here or in somebody else's tab.
   */
  notify(change: BoardChange): void {
    this.emit(change);
  }

  private emit(change: BoardChange): void {
    for (const listener of this.listeners) listener(change);
  }
}

/* ── binding the port to the tRPC client ──────────────────────────────────── */

/**
 * The tRPC surface `createBoardApi` needs, structurally. Typing it here rather than importing
 * `AppRouter`'s inferred client keeps this module compiling while `apps/api` is still moving,
 * and `createBoardApi(createTrpcClient())` still type-checks at the call site.
 */
export interface TrpcLike {
  /** P-122 — optional: an api build without the procedure simply has no vocabulary. */
  tags?: {
    list: { query(input: unknown): Promise<ApiTag[]> };
    createOrGet: { mutate(input: unknown): Promise<ApiTag> };
  };
  boards: {
    read: { query(input: unknown): Promise<ApiBoardRead> };
    nextItems: { query(input: unknown): Promise<{ items: ApiItem[]; cursor: string | null }> };
    members: { query(input: unknown): Promise<Person[]> };
    facets: { query(input: unknown): Promise<Record<string, FacetValue[]>> };
  };
  views: {
    list: { query(input: unknown): Promise<ApiView[]> };
    userState: { query(input: unknown): Promise<Record<string, unknown> | null> };
    setUserState: { mutate(input: unknown): Promise<unknown> };
    update: { mutate(input: unknown): Promise<unknown> };
  };
  cells: {
    /** The second argument is tRPC's per-call options; `context.mutationId` rides on it. */
    setMany: { mutate(input: unknown, options?: unknown): Promise<ApiCellWrite[]> };
    /** P-110 — a whole range in one call. */
    setBatch: {
      mutate(
        input: unknown,
        options?: unknown,
      ): Promise<{ changed: number; results: (ApiCellWrite & { itemId: string })[] }>;
    };
    /** P-128 — the button press and the timer verbs. */
    clickButton: { mutate(input: unknown): Promise<unknown> };
    timer: {
      start: { mutate(input: unknown): Promise<unknown> };
      stop: { mutate(input: unknown): Promise<unknown> };
      addSession: { mutate(input: unknown): Promise<unknown> };
    };
  };
  items: {
    create: { mutate(input: unknown): Promise<ApiItem> };
    rename: { mutate(input: unknown): Promise<ApiItem> };
    subitemsOf: {
      query(input: unknown): Promise<{
        boardId: string | null;
        columns: ApiColumn[];
        byParent: Record<string, ApiItem[]>;
      }>;
    };
    createSubitem: { mutate(input: unknown): Promise<ApiItem> };
    bulkDelete: { mutate(input: unknown): Promise<ApiBulkResult> };
    bulkArchive: { mutate(input: unknown): Promise<ApiBulkResult> };
    undoBulk: { mutate(input: unknown): Promise<unknown> };
    restore: { mutate(input: unknown): Promise<unknown> };
    unarchive: { mutate(input: unknown): Promise<unknown> };
    bulkDuplicate: { mutate(input: unknown): Promise<unknown> };
    bulkMove: { mutate(input: unknown): Promise<unknown> };
    bulkReorder: { mutate(input: unknown): Promise<unknown> };
    reorder: { mutate(input: unknown): Promise<unknown> };
    move: { mutate(input: unknown): Promise<unknown> };
    moveToBoard: { mutate(input: unknown): Promise<unknown> };
    convertItemToSubitem: { mutate(input: unknown): Promise<unknown> };
    moveSubitemToParent: { mutate(input: unknown): Promise<unknown> };
    convertSubitemToItem: { mutate(input: unknown): Promise<unknown> };
  };
  groups: {
    setCollapsed: { mutate(input: unknown): Promise<unknown> };
    collapsed: { query(input: unknown): Promise<never> };
    create: { mutate(input: unknown): Promise<unknown> };
    rename: { mutate(input: unknown): Promise<unknown> };
    setColor: { mutate(input: unknown): Promise<unknown> };
    duplicate: { mutate(input: unknown): Promise<unknown> };
    move: { mutate(input: unknown): Promise<unknown> };
    archive: { mutate(input: unknown): Promise<unknown> };
    remove: { mutate(input: unknown): Promise<unknown> };
  };
  columns: {
    create: { mutate(input: unknown): Promise<ApiColumn> };
    update: { mutate(input: unknown): Promise<ApiColumn> };
    updateSettings: { mutate(input: unknown): Promise<ApiColumn> };
    remove: { mutate(input: unknown): Promise<unknown> };
    restore: { mutate(input: unknown): Promise<unknown> };
    duplicate: { mutate(input: unknown): Promise<ApiColumn> };
    move: { mutate(input: unknown): Promise<unknown> };
  };
  boards2?: never;
}

/**
 * `add_file_to_column` (P-128). monday serves it on a **multipart** route rather than through
 * the ordinary mutation path — *"files can only be added through the `add_file_to_column`
 * mutation"* `[09§A.7 file]` — and so do we: the same `POST /files/upload` the item card's
 * Files tab uses (`item-card/trpc-source.ts`), with `column_id` naming the cell.
 */
export const CELL_FILE_UPLOAD_ENDPOINT = '/files/upload';

async function uploadToColumn(input: { itemId: string; columnId: string; file: File }): Promise<unknown> {
  const form = new FormData();
  form.append('item_id', input.itemId);
  form.append('column_id', input.columnId);
  form.append('file', input.file, input.file.name);
  const response = await fetch(CELL_FILE_UPLOAD_ENDPOINT, {
    method: 'POST',
    body: form,
    credentials: 'same-origin',
    // The CSRF guard every state-changing browser call carries (M0-F3 §5.3).
    headers: { 'x-elizmonday-client': 'web' },
  });
  if (!response.ok) throw new Error(`Upload failed with ${String(response.status)}`);
  return (await response.json()) as unknown;
}

/** Round 3's one-liner: `new TrpcBoardDataSource(createBoardApi(client), me)`. */
export function createBoardApi(trpc: TrpcLike): BoardApi {
  return {
    readBoard: (input) => trpc.boards.read.query(input),
    nextItems: (input) => trpc.boards.nextItems.query(input),
    listViews: (input) => trpc.views.list.query(input),
    viewUserState: (input) => trpc.views.userState.query(input),
    setViewUserState: (input) => trpc.views.setUserState.mutate(input),
    updateView: (input) => trpc.views.update.mutate(input),
    boardMembers: (input) => trpc.boards.members.query({ id: input.boardId }),
    ...(trpc.tags
      ? {
          accountTags: (input) => trpc.tags!.list.query({ boardId: input.boardId }),
          createTag: (input) => trpc.tags!.createOrGet.mutate({ boardId: input.boardId, name: input.name }),
        }
      : {}),
    clickButton: (input) => trpc.cells.clickButton.mutate(input),
    startTimer: (input) => trpc.cells.timer.start.mutate(input),
    stopTimer: (input) => trpc.cells.timer.stop.mutate(input),
    addTimerSession: (input) => trpc.cells.timer.addSession.mutate(input),
    uploadFile: (input) => uploadToColumn(input),
    facets: (input) => trpc.boards.facets.query(input),
    setCells: ({ mutationId, ...input }) =>
      trpc.cells.setMany.mutate(input, mutationId ? { context: { mutationId } } : undefined),
    setCellsBatch: ({ mutationId, ...input }) =>
      trpc.cells.setBatch.mutate(input, mutationId ? { context: { mutationId } } : undefined),
    createItem: (input) => trpc.items.create.mutate(input),
    renameItem: (input) => trpc.items.rename.mutate(input),
    subitemsOf: (input) => trpc.items.subitemsOf.query(input),
    createSubitem: (input) => trpc.items.createSubitem.mutate(input),
    removeItems: (input) => trpc.items.bulkDelete.mutate(input),
    archiveItems: (input) => trpc.items.bulkArchive.mutate(input),
    undoBulkItems: (input) => trpc.items.undoBulk.mutate(input),
    restoreItem: (input) => trpc.items.restore.mutate(input),
    unarchiveItem: (input) => trpc.items.unarchive.mutate(input),
    duplicateItems: (input) => trpc.items.bulkDuplicate.mutate(input),
    moveItems: (input) => trpc.items.bulkMove.mutate(input),
    reorderItems: (input) => trpc.items.bulkReorder.mutate(input),
    moveItemToBoard: (input) => trpc.items.moveToBoard.mutate(input),
    convertItemToSubitem: (input) => trpc.items.convertItemToSubitem.mutate(input),
    moveSubitemToParent: (input) => trpc.items.moveSubitemToParent.mutate(input),
    convertSubitemToItem: (input) => trpc.items.convertSubitemToItem.mutate(input),
    setGroupCollapsed: (input) => trpc.groups.setCollapsed.mutate(input),
    collapsedGroupIds: (input) => trpc.groups.collapsed.query(input),
    createGroup: (input) => trpc.groups.create.mutate(input),
    renameGroup: (input) => trpc.groups.rename.mutate(input),
    setGroupColor: (input) => trpc.groups.setColor.mutate(input),
    duplicateGroup: (input) => trpc.groups.duplicate.mutate(input),
    moveGroup: (input) => trpc.groups.move.mutate(input),
    archiveGroup: (input) => trpc.groups.archive.mutate(input),
    removeGroup: (input) => trpc.groups.remove.mutate(input),
    reorderItem: (input) => trpc.items.reorder.mutate(input),
    moveItemToGroup: (input) => trpc.items.move.mutate(input),
    createColumn: (input) => trpc.columns.create.mutate(input),
    updateColumn: (input) => trpc.columns.update.mutate(input),
    updateColumnSettings: (input) => trpc.columns.updateSettings.mutate(input),
    removeColumn: (input) => trpc.columns.remove.mutate({ boardId: input.boardId, id: input.id }),
    restoreColumn: (input) => trpc.columns.restore.mutate({ boardId: input.boardId, id: input.id }),
    duplicateColumn: (input) => trpc.columns.duplicate.mutate({ boardId: input.boardId, id: input.id }),
    moveColumn: (input) => trpc.columns.move.mutate(input),
  };
}
